/**
 * ResponseTraceDrawer — §22.2 Pipeline Debug
 *
 * Click any response → see all pipeline stages with timing.
 * Shows stage name, duration, input/output summary, status.
 */

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  CheckCircle, XCircle, Clock, ChevronRight,
  Shield, Brain, Search, Wrench, FileText, Sparkles,
  AlertTriangle, BarChart3,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useState } from 'react';

interface PipelineStage {
  name: string;
  durationMs: number;
  status: 'success' | 'error' | 'skipped';
  error?: string;
  details?: Record<string, unknown>;
}

interface ResponseTraceDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stages: PipelineStage[];
  totalLatencyMs: number;
  model?: string;
  intent?: string;
  ragSourceCount?: number;
  confidenceScore?: number;
}

export function ResponseTraceDrawer({
  open, onOpenChange, stages, totalLatencyMs,
  model, intent, ragSourceCount, confidenceScore,
}: ResponseTraceDrawerProps) {
  const pipelineMs = stages.reduce((sum, s) => sum + s.durationMs, 0);
  const llmMs = totalLatencyMs - pipelineMs;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px] sm:w-[500px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Pipeline Trace
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] mt-4">
          <div className="space-y-4 pr-4">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="space-y-1">
                <span className="text-muted-foreground">Total Latency</span>
                <p className="font-bold">{(totalLatencyMs / 1000).toFixed(2)}s</p>
              </div>
              <div className="space-y-1">
                <span className="text-muted-foreground">Pipeline Overhead</span>
                <p className="font-bold">{(pipelineMs / 1000).toFixed(2)}s</p>
              </div>
              {model && (
                <div className="space-y-1">
                  <span className="text-muted-foreground">Model</span>
                  <p className="font-mono text-xs">{model}</p>
                </div>
              )}
              {intent && (
                <div className="space-y-1">
                  <span className="text-muted-foreground">Intent</span>
                  <Badge variant="outline">{intent}</Badge>
                </div>
              )}
              {ragSourceCount != null && (
                <div className="space-y-1">
                  <span className="text-muted-foreground">RAG Sources</span>
                  <p className="font-bold">{ragSourceCount}</p>
                </div>
              )}
              {confidenceScore != null && (
                <div className="space-y-1">
                  <span className="text-muted-foreground">Confidence</span>
                  <Badge variant={confidenceScore >= 70 ? 'default' : confidenceScore >= 40 ? 'secondary' : 'destructive'}>
                    {confidenceScore}
                  </Badge>
                </div>
              )}
            </div>

            {/* Waterfall */}
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Pipeline Stages</h3>
              {stages.map((stage, i) => (
                <StageRow key={i} stage={stage} totalMs={totalLatencyMs} />
              ))}
              {/* LLM generation (not a filter, shown separately) */}
              <div className="flex items-center gap-2 py-2 px-3 rounded-md bg-primary/5 border">
                <Brain className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium flex-1">LLM Generation</span>
                <span className="text-xs text-muted-foreground">{Math.max(0, llmMs)}ms</span>
                <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, (llmMs / totalLatencyMs) * 100)}%` }} />
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

function StageRow({ stage, totalMs }: { stage: PipelineStage; totalMs: number }) {
  const [expanded, setExpanded] = useState(false);
  const icon = STAGE_ICONS[stage.name] ?? <Sparkles className="h-3.5 w-3.5" />;
  const pct = totalMs > 0 ? (stage.durationMs / totalMs) * 100 : 0;

  return (
    <div>
      <button
        onClick={() => stage.details && setExpanded(!expanded)}
        className={cn(
          'flex items-center gap-2 py-1.5 px-3 rounded-md w-full text-left hover:bg-muted/50 transition-colors',
          stage.status === 'error' && 'bg-destructive/5',
          stage.status === 'skipped' && 'opacity-50',
        )}
      >
        <span className="shrink-0">{icon}</span>
        <span className="text-xs flex-1 truncate">{formatStageName(stage.name)}</span>
        {stage.status === 'error' && <XCircle className="h-3 w-3 text-destructive shrink-0" />}
        {stage.status === 'success' && <CheckCircle className="h-3 w-3 text-green-500 shrink-0" />}
        <span className="text-[10px] text-muted-foreground w-12 text-right shrink-0">{stage.durationMs}ms</span>
        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden shrink-0">
          <div
            className={cn('h-full rounded-full', stage.status === 'error' ? 'bg-destructive' : 'bg-primary/60')}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
        {stage.details && <ChevronRight className={cn('h-3 w-3 text-muted-foreground transition-transform', expanded && 'rotate-90')} />}
      </button>
      {expanded && stage.details && (
        <div className="ml-8 px-3 py-2 text-xs font-mono bg-muted/30 rounded mb-1">
          <pre className="whitespace-pre-wrap overflow-x-auto max-h-32">{JSON.stringify(stage.details, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function formatStageName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const STAGE_ICONS: Record<string, React.ReactNode> = {
  template_variables: <FileText className="h-3.5 w-3.5 text-blue-500" />,
  user_memory_recall: <Brain className="h-3.5 w-3.5 text-purple-500" />,
  guardrails_inlet: <Shield className="h-3.5 w-3.5 text-red-500" />,
  entity_extraction: <Search className="h-3.5 w-3.5 text-orange-500" />,
  ambiguity_detection: <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />,
  intent_classification: <Sparkles className="h-3.5 w-3.5 text-blue-500" />,
  session_entity_tracking: <Search className="h-3.5 w-3.5 text-indigo-500" />,
  query_rewriting: <FileText className="h-3.5 w-3.5 text-teal-500" />,
  conversation_history: <Clock className="h-3.5 w-3.5 text-gray-500" />,
  rag_retrieval: <Search className="h-3.5 w-3.5 text-green-500" />,
  context_summarization: <FileText className="h-3.5 w-3.5 text-amber-500" />,
  tool_guard: <Shield className="h-3.5 w-3.5 text-orange-500" />,
  system_prompt_assembly: <Wrench className="h-3.5 w-3.5 text-gray-500" />,
  faithfulness_check: <CheckCircle className="h-3.5 w-3.5 text-green-500" />,
  confidence_scoring: <BarChart3 className="h-3.5 w-3.5 text-blue-500" />,
  follow_up_generation: <Sparkles className="h-3.5 w-3.5 text-purple-500" />,
  self_correction: <Wrench className="h-3.5 w-3.5 text-yellow-500" />,
  audit_logging: <FileText className="h-3.5 w-3.5 text-gray-500" />,
};
